import { useEffect, useState, useCallback } from 'react';

import { usePublicLinkData } from '@confluence/public-links/entry-points/usePublicLinkData';
import { isExternalShareRequest } from '@confluence/external-share-utils';

import { PublicLinkService } from './services/whiteboard-public-link-service';
import type { WhiteboardTokenService } from './services/whiteboard-token-service';

export const usePublicLinkService = (contentId: string, tokenService: WhiteboardTokenService) => {
	const [publicLinkService] = useState(() => new PublicLinkService());
	const isExternal = isExternalShareRequest();

	const { isLoading, isPublicLinkFullyEnabled, isEditPublicLink, refetch } = usePublicLinkData({
		contentId,
		contentQueryProps: { skip: true, contentType: 'whiteboard' },
	});

	const getPublicLinkInfoFromGraphql = useCallback(() => {
		if (!isLoading) {
			publicLinkService.setPublicLink({
				isEnabled: isPublicLinkFullyEnabled,
				isEditEnabled: isEditPublicLink,
				refetch,
			});
		}
	}, [publicLinkService, isLoading, isPublicLinkFullyEnabled, isEditPublicLink, refetch]);

	// we can't make these calls on a public link
	useEffect(() => {
		if (!isExternal) {
			getPublicLinkInfoFromGraphql();
		}
	}, [isExternal, isLoading, publicLinkService, getPublicLinkInfoFromGraphql]);

	const getPublicLinkInfoFromToken = useCallback(async () => {
		const token = await tokenService.getToken();
		const permissions = tokenService.getPermissions(token);

		publicLinkService.setPublicLink({
			// really this should never be false by this point, since if we're in isExternal we have to be on a public link
			// and for us to get to this logic, the public link must be enabled. Checking the permissions.length is really just
			// an extra safety net
			isEnabled: permissions.length > 0,
			isEditEnabled: permissions.includes('write'),
			refetch: getPublicLinkInfoFromToken,
		});
	}, [publicLinkService, tokenService]);

	// since we can't make the queries encapsulated in usePublicLinkData, we need to parse the token to determine the public link info
	useEffect(() => {
		if (isExternal) {
			void getPublicLinkInfoFromToken();
		}
	}, [isExternal, getPublicLinkInfoFromToken, publicLinkService]);

	return publicLinkService;
};
